// React
import * as React from "react";
import { useState, useEffect, useContext, useRef } from "react";
import { styled } from "@mui/material/styles";
import AuthContext from "../../../../contexts/AuthContext";
import { ClubsService } from "@/core/http/services/ClubsService";

import {
  Box,
  Button,
  CssBaseline,
  Grid,
  IconButton,
  Snackbar,
  Toolbar,
  Tooltip,
  Typography,
} from "@mui/material";
import Drawer from "@mui/material/Drawer";
import AppBar from "@mui/material/AppBar";

// Icons
import MenuIcon from "@mui/icons-material/Menu";
import LogoutIcon from "@mui/icons-material/Logout";
import DarkModeIcon from "@mui/icons-material/DarkMode";
import LightModeIcon from "@mui/icons-material/LightMode";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";

import { Outlet, useLocation, useNavigate, useParams } from "react-router-dom";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import BaseSideBar from "./sidebar/BaseSideBar";
import { useThemeMode } from "../../../../contexts/ThemeContext";
import { PwaInstallProvider } from "@/core/pwa";

const INVITE_LOGIN_BASE_URL = "https://apeakclubmc.codx.dev.br/login";

const isSameClubId = (left, right) =>
  left !== undefined &&
  left !== null &&
  right !== undefined &&
  right !== null &&
  String(left) === String(right);

const getClubLogo = (club, mode) => {
  if (!club) return null;

  const lightLogo =
    club?.logoLightUrl || club?.logoLight || club?.logoLightMode || null;
  const darkLogo =
    club?.logoDarkUrl || club?.logoDark || club?.logoDarkMode || null;

  return mode === "dark" ? darkLogo || lightLogo : lightLogo || darkLogo;
};

const copyTextToClipboard = async (text) => {
  if (!text) return false;

  try {
    if (navigator?.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    // fallback below
  }

  try {
    const textarea = document.createElement("textarea");
    textarea.value = text;
    textarea.setAttribute("readonly", "");
    textarea.style.position = "fixed";
    textarea.style.top = "-1000px";
    textarea.style.left = "-1000px";
    document.body.appendChild(textarea);
    textarea.select();
    textarea.setSelectionRange(0, textarea.value.length);
    const ok = document.execCommand("copy");
    document.body.removeChild(textarea);
    return ok;
  } catch {
    return false;
  }
};

export default function PersistentDrawerLeft() {
  // States
  const [sidebarExpanded, setSidebarExpanded] = useState(true);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [clubDetails, setClubDetails] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: "" });

  // Uses
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const { mode, toggleMode } = useThemeMode();
  const { userLoggedData, logout, isAuthLoading, updateAuthPatch } =
    useContext(AuthContext);
  const { clubId: clubIdFromUrl } = useParams();
  const resolvedClubId =
    clubIdFromUrl || userLoggedData?.selectedClubId || null;
  const clubs = Array.isArray(userLoggedData?.clubs)
    ? userLoggedData.clubs
    : [];
  const resolvedClub =
    clubs.find((c) => isSameClubId(c?.id, resolvedClubId)) || clubs[0] || null;
  const role = userLoggedData?.role?.toUpperCase?.() || "";
  const clubDetailsRequestRef = useRef(0);
  const authSnapshotRef = useRef({ userLoggedData: null, updateAuthPatch });

  // useEffects
  useEffect(() => {
    authSnapshotRef.current = { userLoggedData, updateAuthPatch };
  }, [userLoggedData, updateAuthPatch]);

  useEffect(() => {
    if (isAuthLoading) {
      return;
    }
    const role = userLoggedData?.role?.toUpperCase?.() || "";
    if (!userLoggedData) {
      navigate("/login");
      return;
    }
    if (role === "SUPER_ADMIN" && !resolvedClubId) {
      navigate("/super-admin/clubs");
      return;
    }
    if (role !== "ADMIN" && role !== "SUPER_ADMIN") {
      navigate("/login");
    }
  }, [userLoggedData, isAuthLoading, navigate, resolvedClubId]);

  useEffect(() => {
    if (!isMobile) {
      setMobileOpen(false);
    }
  }, [isMobile]);

  useEffect(() => {
    const fetchClubDetails = async () => {
      if (isAuthLoading || !userLoggedData) {
        return;
      }

      if (!resolvedClubId) {
        setClubDetails(null);
        return;
      }

      const requestId = clubDetailsRequestRef.current + 1;
      clubDetailsRequestRef.current = requestId;

      try {
        const { status, data } = await ClubsService.getById(resolvedClubId);
        if (clubDetailsRequestRef.current !== requestId) return;

        if (status === 200 && data) {
          setClubDetails(data);

          // Best-effort cache into auth context so other areas can reuse logo URLs.
          const latestUserLoggedData = authSnapshotRef.current.userLoggedData;
          const latestUpdateAuthPatch = authSnapshotRef.current.updateAuthPatch;
          const prevClubs = Array.isArray(latestUserLoggedData?.clubs)
            ? latestUserLoggedData.clubs
            : [];
          if (prevClubs.length > 0) {
            const currentClub =
              prevClubs.find((club) => isSameClubId(club?.id, data.id)) || null;
            const changed =
              !currentClub ||
              currentClub?.name !== data?.name ||
              currentClub?.logoLightUrl !== data?.logoLightUrl ||
              currentClub?.logoDarkUrl !== data?.logoDarkUrl ||
              currentClub?.inviteToken !== data?.inviteToken;

            if (changed) {
              const nextClubs = prevClubs.map((club) =>
                isSameClubId(club?.id, data.id) ? { ...club, ...data } : club,
              );
              await latestUpdateAuthPatch?.({ clubs: nextClubs });
            }
          }
        } else {
          setClubDetails(null);
        }
      } catch {
        if (clubDetailsRequestRef.current === requestId) {
          setClubDetails(null);
        }
      }
    };

    fetchClubDetails();
  }, [resolvedClubId, isAuthLoading, userLoggedData?.id]);

  const handleDrawerToggle = () => {
    if (isMobile) {
      setMobileOpen((prev) => !prev);
      return;
    }
    setSidebarExpanded((prev) => !prev);
  };

  // await data loading
  if (!userLoggedData) return null;

  const drawerOpen = isMobile ? mobileOpen : true;
  const drawerCurrentWidth = isMobile
    ? drawerWidth
    : sidebarExpanded
      ? drawerWidth
      : miniDrawerWidth;
  const sidebarContentOpen = isMobile ? true : sidebarExpanded;
  const clubLogoCandidate = clubDetails || resolvedClub;
  const clubLogo = getClubLogo(clubLogoCandidate, mode);
  const headerLogo = clubLogo;
  const inviteToken =
    clubLogoCandidate?.inviteToken || clubDetails?.inviteToken || null;
  const currentSection = location.pathname.split("/").filter(Boolean).pop();

  const handleCopyInviteLink = async () => {
    if (!inviteToken) {
      setSnackbar({
        open: true,
        message: "Invite token não encontrado para este clube.",
      });
      return;
    }
    const inviteLink = `${INVITE_LOGIN_BASE_URL}/${inviteToken}`;
    const ok = await copyTextToClipboard(inviteLink);
    setSnackbar({
      open: true,
      message: ok
        ? "Link de convite copiado!"
        : "Não foi possível copiar o link.",
    });
  };

  return (
    <PwaInstallProvider
      club={clubLogoCandidate}
      bottomOffset={24}
      hasReachedClubHome={currentSection === "dashboard"}
    >
      <Box
        sx={{
          width: "100%",
          height: "100dvh",
          display: "flex",
          flexDirection: "column",
          bgcolor: "background.default",
          overflow: "hidden",
        }}
      >
        <CssBaseline />

        <AppBar
          position="sticky"
          elevation={0}
          sx={{
            bgcolor: "background.default",
            borderBottom: "none",
          }}
        >
        <Toolbar
          sx={{
            display: "flex",
            justifyContent: "space-between",
            gap: 2,
            minHeight: { xs: 64, sm: 72 },
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", minWidth: 0 }}>
            <IconButton
              aria-label="abrir menu"
              onClick={handleDrawerToggle}
              edge="start"
              sx={{ mr: 1.5, display: isMobile ? "inline-flex" : "none" }}
            >
              <MenuIcon />
            </IconButton>
            {headerLogo ? (
              <Box
                component="img"
                src={headerLogo}
                alt="Logo do clube"
                sx={{
                  height: { xs: 34, sm: 38 },
                  objectFit: "contain",
                  mr: 1.4,
                }}
              />
            ) : null}
            <Typography
              variant="h5"
              fontWeight="bold"
              sx={{
                fontFamily: "'Audiowide', cursive",
                letterSpacing: { xs: 1, sm: 2 },
                color: "text.primary",
                fontSize: { xs: "1.2rem", sm: "1.5rem" },
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            ></Typography>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            {role === "SUPER_ADMIN" ? (
              <Button
                onClick={() => navigate("/super-admin/clubs")}
                variant="outlined"
                sx={{
                  display: { xs: "none", sm: "inline-flex" },
                  textTransform: "none",
                  fontWeight: 800,
                  borderRadius: 2,
                }}
              >
                Trocar clube
              </Button>
            ) : role === "ADMIN" ? (
              <Button
                onClick={handleCopyInviteLink}
                variant="outlined"
                sx={{
                  display: { xs: "none", sm: "inline-flex" },
                  textTransform: "none",
                  fontWeight: 800,
                  borderRadius: 2,
                }}
              >
                Copiar link de convite
              </Button>
            ) : null}
            <Tooltip title={mode === "dark" ? "Modo claro" : "Modo escuro"}>
              <IconButton
                aria-label="alternar tema"
                onClick={toggleMode}
                sx={{
                  bgcolor: "action.hover",
                  color: "text.primary",
                  "&:hover": { bgcolor: "action.selected" },
                }}
              >
                {mode === "dark" ? <LightModeIcon /> : <DarkModeIcon />}
              </IconButton>
            </Tooltip>
            <Button
              onClick={logout}
              variant="contained"
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.25,
                bgcolor: "primary.main",
                color: "primary.contrastText",
                "&:hover": { bgcolor: "primary.dark" },
                px: { xs: 1.5, sm: 3 },
                minWidth: "auto",
              }}
            >
              <Typography
                variant="body2"
                sx={{ display: { xs: "none", sm: "block" } }}
              >
                Sair
              </Typography>
              <LogoutIcon fontSize="small" />
            </Button>
          </Box>
        </Toolbar>
        </AppBar>

        <Box
          sx={{ display: "flex", flex: 1, minHeight: 0, overflow: "hidden" }}
        >
          <Drawer
            sx={{
              width: drawerCurrentWidth,
              height: "100%",
              flexShrink: 0,
              "&.MuiDrawer-docked": {
                height: "100%",
                width: drawerCurrentWidth,
              },
              "& .MuiDrawer-paper": {
                width: drawerCurrentWidth,
                boxSizing: "border-box",
                border: "1px solid",
                borderColor: "divider",
                position: "relative",
                height: isMobile ? "100%" : "calc(100% - 16px)",
                mt: isMobile ? 0 : 1,
                ml: isMobile ? 0 : 1,
                borderRadius: isMobile ? 0 : "12px",
                overflowX: "hidden",
                bgcolor: "background.paper",
                transition: theme.transitions.create("width", {
                  easing: theme.transitions.easing.sharp,
                  duration: theme.transitions.duration.standard,
                }),
              },
            }}
            variant={isMobile ? "temporary" : "permanent"}
            anchor="left"
            open={drawerOpen}
            onClose={handleDrawerToggle}
            ModalProps={{ keepMounted: true }}
          >
            <BaseSideBar
              logout={logout}
              userRole={userLoggedData.role || "N"}
              clubId={resolvedClubId}
              clubLogo={clubLogo}
              open={sidebarContentOpen}
              isMobile={isMobile}
              onToggleSidebar={handleDrawerToggle}
              LOGGED_USER_NAME={userLoggedData.mkUser}
            />
          </Drawer>

          <Main>
          <Box
            component="main"
            elevation={4}
            sx={{
              flex: 1,
              bgcolor: "background.default",
              minHeight: 0,
              borderRadius: { xs: 0, sm: "15px" },
              overflowY: "auto",
              overflowX: "hidden",
            }}
          >
            <Grid
              container
              sx={{
                p: { xs: 1.5, sm: 2, md: 3 },
              }}
            >
              <Grid item xs={12}>
                <Outlet />
              </Grid>
            </Grid>
          </Box>
          </Main>
        </Box>

        <Snackbar
          open={snackbar.open}
          autoHideDuration={2500}
          onClose={(_, reason) => {
            if (reason === "clickaway") return;
            setSnackbar((prev) => ({ ...prev, open: false }));
          }}
          message={snackbar.message}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        />

        <Tooltip title="Suporte via WhatsApp" placement="left">
          <IconButton
            component="a"
            href="https://wa.me/54992065346?text=Ol%C3%A1%2C%20necessito%20de%20ajuda%20com%20o%20Apeak%20Club"
            target="_blank"
            rel="noopener noreferrer"
            sx={{
              position: "fixed",
              bottom: 24,
              right: 24,
              bgcolor: "#25D366",
              color: "#fff",
              width: 56,
              height: 56,
              boxShadow: 4,
              zIndex: 1300,
              "&:hover": { bgcolor: "#1ebe5d" },
            }}
          >
            <WhatsAppIcon fontSize="medium" />
          </IconButton>
        </Tooltip>
      </Box>
    </PwaInstallProvider>
  );
}

// Theme

const drawerWidth = 240;
const miniDrawerWidth = 72;

const Main = styled("main")(({ theme }) => ({
  flexGrow: 1,
  minWidth: 0,
  minHeight: 0,
  display: "flex",
  flexDirection: "column",
  overflow: "hidden",
  padding: theme.spacing(2),
}));
